import { datePl, formatMoney, type PaymentClaimInfo, type PaymentReminderInfo, type PaymentReminderPreviewDto, type PaymentReminderRequestInfo, todayInWarsaw } from '@bqcrm/shared';
import { Alert, Button, Group, Modal, Stack, Text, TextInput } from '@mantine/core';
import { IconCash, IconMailForward } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../api';
import { formatDateTime } from '../format';
import { notifyError, notifyOk } from '../notify';
import { IssueWarnings } from './IssueWarnings';
import { MoneyInput } from './Money';

/** Ostatnie przypomnienie przy pozycji harmonogramu („przypomniano 12.11.2026 14:05 (A. Nowak)”). */
export function LastReminder({ r }: { r: PaymentReminderInfo | null | undefined }) {
  if (!r) return null;
  return (
    <Text size="xs" c={r.status === 'blad' || r.status === 'blad_trwaly' ? 'var(--app-text-red)' : 'dimmed'}>
      {r.auto ? 'przypomniano automatycznie' : 'przypomniano'} {formatDateTime(r.at)}
      {!r.auto && r.byName ? ` (${r.byName})` : ''}
      {r.status === 'blad' || r.status === 'blad_trwaly' ? ' — błąd wysyłki' : ''}
    </Text>
  );
}

/**
 * P4: „Przypomnij klientowi o płatności” — podgląd treści (kwota, termin, rachunek) i wysyłka e-mailem z firmy umowy.
 * Wpis w historii sprawy; ostatnie przypomnienie widać przy pozycji.
 */
export function PaymentReminderButton({ itemId, label = 'Przypomnij klientowi', onSent }: { itemId: string; label?: string; onSent?: () => void }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button size="compact-xs" variant="subtle" leftSection={<IconMailForward size={14} />} onClick={() => setOpen(true)}>
        {label}
      </Button>
      <Modal opened={open} onClose={() => setOpen(false)} title="Przypomnienie o płatności" size="lg">
        {open && <ReminderForm itemId={itemId} onClose={() => setOpen(false)} onSent={onSent} />}
      </Modal>
    </>
  );
}

function ReminderForm({ itemId, onClose, onSent }: { itemId: string; onClose: () => void; onSent?: () => void }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['payment-reminder', itemId], queryFn: () => api<PaymentReminderPreviewDto>(`/payment-items/${itemId}/reminder`) });
  const send = useMutation({
    mutationFn: () => api<PaymentReminderPreviewDto>(`/payment-items/${itemId}/reminder`, { method: 'POST' }),
    onSuccess: (r) => {
      notifyOk(`Wysłano przypomnienie do ${r.to ?? 'klienta'}`);
      for (const key of ['receivables', 'contract', 'case-timeline', 'payment-reminder']) void qc.invalidateQueries({ queryKey: [key] });
      onSent?.();
      onClose();
    },
    onError: notifyError,
  });
  const p = q.data;
  if (q.error) return <Alert color="red">{(q.error as Error).message}</Alert>;
  if (!p) return <Text size="sm">Ładowanie…</Text>;
  const blocked = !p.to || p.remainingGr <= 0;
  return (
    <Stack gap="sm">
      <Text size="sm">
        Do: <b>{p.to ?? '— brak adresu e-mail —'}</b>
      </Text>
      <Text size="sm">Temat: {p.subject}</Text>
      <Text size="sm" style={{ whiteSpace: 'pre-wrap', background: 'var(--mantine-color-default-hover)', padding: 8, borderRadius: 4 }}>
        {p.text}
      </Text>
      {p.warnings.length > 0 && (
        <Alert color="orange" variant="light" p="xs">
          <IssueWarnings items={p.warnings} />
        </Alert>
      )}
      {p.lastReminder && <LastReminder r={p.lastReminder} />}
      <Group justify="flex-end">
        <Button variant="default" onClick={onClose}>
          Anuluj
        </Button>
        <Button leftSection={<IconMailForward size={16} />} disabled={blocked} loading={send.isPending} onClick={() => send.mutate()}>
          Wyślij przypomnienie
        </Button>
      </Group>
    </Stack>
  );
}

const warsawDay = (d: Date) => d.toLocaleDateString('sv-SE', { timeZone: 'Europe/Warsaw' });
const sameDay = (iso: string) => warsawDay(new Date(iso)) === warsawDay(new Date());

/**
 * QA P24: opiekun bez prawa wysyłki — „Poproś księgowość o przypomnienie” (powiadomienie dla księgowości,
 * wpis w historii sprawy). Po wysłanej prośbie — informacja, kto i kiedy prosił.
 */
export function ReminderRequestButton({ itemId, request, lastReminder }: { itemId: string; request: PaymentReminderRequestInfo | null; lastReminder?: PaymentReminderInfo | null }) {
  const qc = useQueryClient();
  const ask = useMutation({
    mutationFn: () => api<{ at: string }>(`/payment-items/${itemId}/reminder-request`, { method: 'POST' }),
    onSuccess: () => {
      notifyOk('Księgowość dostała prośbę o przypomnienie klientowi');
      for (const key of ['receivables', 'case-receivables', 'case-timeline']) void qc.invalidateQueries({ queryKey: [key] });
    },
    onError: notifyError,
  });
  if (request)
    return (
      <Text size="xs" c="dimmed">
        poproszono księgowość {formatDateTime(request.at)}
        {request.byName ? ` (${request.byName})` : ''}
      </Text>
    );
  // QA P21: klientowi przypomniano dziś — prośba tego samego dnia jest odrzucana (serwer wyjaśnia), więc bez przycisku
  if (lastReminder && sameDay(lastReminder.at))
    return (
      <Text size="xs" c="dimmed">
        klientowi przypomniano dziś — o kolejne przypomnienie możesz poprosić jutro
      </Text>
    );
  return (
    <Stack gap={0} align="flex-start">
      <Button size="compact-xs" variant="subtle" leftSection={<IconMailForward size={14} />} loading={ask.isPending} onClick={() => ask.mutate()}>
        Poproś księgowość o przypomnienie
      </Button>
      <Text size="xs" c="dimmed">
        przypomnienie e-mailem wysyła księgowość
      </Text>
    </Stack>
  );
}

/** QA qa10311123 3.4: oznaczenie „wpłata zgłoszona DD.MM — czeka na zaksięgowanie” (kto zgłosił, kwota, notatka). */
export function PaymentClaimNote({ claim }: { claim: PaymentClaimInfo | null | undefined }) {
  if (!claim) return null;
  return (
    <Text size="xs" c="orange.8">
      wpłata zgłoszona {formatDateTime(claim.at)}
      {claim.byName ? ` (${claim.byName})` : ''} — czeka na zaksięgowanie
      {claim.amountGr !== null ? ` · ${formatMoney(claim.amountGr)}` : ''}
      {claim.paidAt ? ` z ${datePl(claim.paidAt)}` : ''}
      {claim.note ? ` · ${claim.note}` : ''}
    </Text>
  );
}

/**
 * QA qa10311123 3.4: „Klient zgłosił wpłatę” — opiekun odnotowuje informację od klienta (data, kwota, notatka).
 * Wpłaty nie księguje: księgowość dostaje powiadomienie, a pozycja ma oznaczenie do czasu zaksięgowania.
 */
export function ClientPaidButton({ itemId, remainingGr }: { itemId: string; remainingGr: number }) {
  const [opened, setOpened] = useState(false);
  return (
    <>
      <Button size="compact-xs" variant="subtle" leftSection={<IconCash size={14} />} onClick={() => setOpened(true)}>
        Klient zgłosił wpłatę
      </Button>
      <Modal opened={opened} onClose={() => setOpened(false)} title="Klient zgłosił wpłatę">
        {opened && <ClientPaidForm itemId={itemId} remainingGr={remainingGr} onClose={() => setOpened(false)} />}
      </Modal>
    </>
  );
}

function ClientPaidForm({ itemId, remainingGr, onClose }: { itemId: string; remainingGr: number; onClose: () => void }) {
  const qc = useQueryClient();
  const [paidAt, setPaidAt] = useState(todayInWarsaw());
  const [amountGr, setAmountGr] = useState<number | null>(null);
  const [note, setNote] = useState('');
  const save = useMutation({
    mutationFn: () => api<PaymentClaimInfo>(`/payment-items/${itemId}/client-paid`, { method: 'POST', body: { paidAt, amountGr, note } }),
    onSuccess: () => {
      notifyOk('Księgowość dostała informację o wpłacie — pozycja czeka na zaksięgowanie');
      for (const key of ['receivables', 'case-receivables', 'case-timeline', 'start-conditions']) void qc.invalidateQueries({ queryKey: [key] });
      onClose();
    },
    onError: notifyError,
  });
  return (
    <Stack>
      <Text size="sm">Wpłaty nie księgujesz — księgowość sprawdzi wyciąg i ją zarejestruje. Do tego czasu pozycja ma oznaczenie „wpłata zgłoszona”.</Text>
      <TextInput label="Data wpłaty (wg klienta)" type="date" max={todayInWarsaw()} value={paidAt} onChange={(e) => setPaidAt(e.currentTarget.value)} />
      <MoneyInput label="Kwota (wg klienta)" placeholder={`opcjonalnie, do zapłaty ${formatMoney(remainingGr)}`} valueGr={amountGr} onChangeGr={setAmountGr} />
      <TextInput label="Notatka" placeholder="opcjonalnie, np. przelew z konta męża" value={note} onChange={(e) => setNote(e.currentTarget.value)} />
      <Group justify="flex-end">
        <Button variant="default" onClick={onClose}>
          Anuluj
        </Button>
        <Button loading={save.isPending} disabled={!paidAt || (amountGr !== null && amountGr > remainingGr)} onClick={() => save.mutate()}>
          Zgłoś księgowości
        </Button>
      </Group>
    </Stack>
  );
}
